import { useState } from "react";
import { Bell, Layers3, Save, SlidersHorizontal } from "lucide-react";
import toast from "react-hot-toast";
import { FormSelect, FormToggle } from "../form/FormField";
import { SettingsSectionHeader } from "./SettingsSectionHeader";
import { LanguageSelector } from "../LanguageSelector";
import {
  loadPartnerUiPreferences,
  savePartnerUiPreferences,
  type PartnerUiPreferences,
} from "../../lib/partnerSettings";
import { useI18n } from "../../i18n";

export function PreferencesSettingsTab() {
  const { t } = useI18n();
  const [prefs, setPrefs] = useState<PartnerUiPreferences>(() =>
    loadPartnerUiPreferences()
  );
  const [saved, setSaved] = useState(prefs);

  const dirty = JSON.stringify(prefs) !== JSON.stringify(saved);

  const onSave = () => {
    savePartnerUiPreferences(prefs);
    setSaved(prefs);
    toast.success(t("settings.preferences.toastSaved"));
  };

  return (
    <section
      className="menu-panel"
      role="tabpanel"
      aria-label={t("settings.tabs.preferences")}
    >
      <SettingsSectionHeader
        title={t("settings.preferences.title")}
        hint={t("settings.preferences.hint")}
        icon={SlidersHorizontal}
        actions={
          <button
            type="button"
            className="btn-primary min-h-[44px] gap-2 px-4"
            disabled={!dirty}
            onClick={onSave}
          >
            <Save className="h-4 w-4" strokeWidth={2.25} />
            {t("common.save")}
          </button>
        }
      />
      <div className="menu-panel__body space-y-5">
        <LanguageSelector variant="panel" />

        <div className="settings-block">
          <div className="settings-block__head">
            <span className="settings-block__icon" aria-hidden>
              <Layers3 className="h-4 w-4" strokeWidth={2.25} />
            </span>
            <div>
              <p className="settings-block__title">
                {t("settings.preferences.stageTitle")}
              </p>
              <p className="settings-block__sub">
                {t("settings.preferences.stageHint")}
              </p>
            </div>
          </div>
          <div className="mt-4">
            <FormSelect
              id="stage-focus"
              value={prefs.defaultStageFocus}
              onChange={(e) =>
                setPrefs((p) => ({
                  ...p,
                  defaultStageFocus: e.target
                    .value as PartnerUiPreferences["defaultStageFocus"],
                }))
              }
            >
              <option value="all">{t("settings.preferences.stageAll")}</option>
              <option value="pending">
                {t("settings.preferences.stagePending")}
              </option>
              <option value="processing">
                {t("settings.preferences.stageProcessing")}
              </option>
              <option value="ready">{t("settings.preferences.stageReady")}</option>
            </FormSelect>
          </div>
        </div>

        <div className="settings-block">
          <div className="settings-block__head">
            <span className="settings-block__icon" aria-hidden>
              <Bell className="h-4 w-4" strokeWidth={2.25} />
            </span>
            <div>
              <p className="settings-block__title">
                {t("settings.preferences.notifyTitle")}
              </p>
              <p className="settings-block__sub">
                {t("settings.preferences.notifyHint")}
              </p>
            </div>
          </div>
          <div className="mt-4 space-y-3">
            <FormToggle
              id="notify-orders"
              checked={prefs.notifyNewOrders}
              onChange={(v) => setPrefs((p) => ({ ...p, notifyNewOrders: v }))}
              label={t("settings.preferences.notifyOrders")}
              description={t("settings.preferences.notifyOrdersDesc")}
            />
            <FormToggle
              id="notify-drivers"
              checked={prefs.notifyDriverUpdates}
              onChange={(v) =>
                setPrefs((p) => ({ ...p, notifyDriverUpdates: v }))
              }
              label={t("settings.preferences.notifyDrivers")}
              description={t("settings.preferences.notifyDriversDesc")}
            />
            <FormToggle
              id="notify-fallback"
              checked={prefs.notifyFallbackAlerts}
              onChange={(v) =>
                setPrefs((p) => ({ ...p, notifyFallbackAlerts: v }))
              }
              label={t("settings.preferences.notifyFallback")}
              description={t("settings.preferences.notifyFallbackDesc")}
            />
          </div>
        </div>
      </div>
    </section>
  );
}
